import { NavigateOptions, To, useLocation, useNavigate } from 'react-router';

export type Breadcrumb = {
  path: string;
  label: string;
};

/**
 * Custom hook for navigation used when you want to start persisting the user's navigation path.
 * When the user navigates to a new page, the current path and label are added to the breadcrumb state.
 * The `rootLabel` is used as the label when we don’t yet have a navigation history (the 0th element).
 * The `toLabel` is used as the label when we know the navigation history (the remaining elements).
 * The persisted breadcrumb state is immediately cleared by using the default `useNavigate` hook.
 * @returns {function} navigateWithBreadcrumbs - Function to navigate with breadcrumbs.
 */
const useNavigateWithBreadcrumbs = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateWithBreadcrumbs = (
    to: To,
    toLabel: string,
    rootLabel: string,
    options?: NavigateOptions,
  ) => {
    const currentBreadcrumbs: Breadcrumb[] = location.state?.breadcrumbs || [];
    const newBreadcrumb = { path: to, label: toLabel };

    // If there are no breadcrumbs, add an initial breadcrumb with the root label
    if (!currentBreadcrumbs.length) {
      currentBreadcrumbs.push({ path: location.pathname, label: rootLabel });
    }

    navigate(to, {
      ...options,
      state: {
        breadcrumbs: [
          ...(options?.replace
            ? currentBreadcrumbs.slice(0, -1)
            : currentBreadcrumbs),
          newBreadcrumb,
        ] as Breadcrumb[],
      },
    });
  };

  return navigateWithBreadcrumbs;
};

export default useNavigateWithBreadcrumbs;
